import React, { useState, useEffect, useContext } from 'react';
import API_ENDPOINTS from '../../../../core/constant/API_ENDPOINTS';
import DataTableWrapper from '../../../admin/common/dataTable/dataTableWrapper';
import ConfirmActionModal from '../../../components/modal/ConfirmActionModal';
import BaseModal from '../../../components/modal/baseModal';
import SuccessModal from '../../../components/modal/SuccessModal';
import ErrorModal from '../../../components/modal/ErrorModal';
import { useOutletContext } from 'react-router-dom';
import { AuthContext } from '../../../../core/context/AuthContext';
import { BreadcrumbContext } from '../../../../core/Interface/contextInterface';
import usePaginatedFetch from '../../../../core/hooks/api/usePaginatedFetch';
import { DatePicker } from '../../../components/inputs';
import usePost from '../../../../core/hooks/api/usePost';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive'; // Import for media query hook
import { emitter } from '../../../../core/event/envents';

/**
 * Componente para mostrar los detalles de pago del cliente.
 * @returns {JSX.Element} Componente de detalles de pago.
 */
const PaymentDetails = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    return <div>Error: Auth context not found!</div>;
  }

  const { user } = authContext;
  const { setBreadcrumbData } = useOutletContext<BreadcrumbContext>();
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [activationDate, setActivationDate] = useState<Date | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);
  const [showFooter, setShowFooter] = useState(false); // New state to manage footer visibility
  const isDesktop = useMediaQuery({ minWidth: 768 }); // Check if the screen width is at least 768px (desktop)

  const { post, loading: loadingModal } = usePost(API_ENDPOINTS.paymentActivate);

  const { data, totalElements, first, rows, onPageChange, refetch } = usePaginatedFetch(
    user ? `${API_ENDPOINTS.paymentDetails}?customerId=${user.userId}` : null,
    10,
    0,
  );

  useEffect(() => {
    setBreadcrumbData({ title: "Detalles de pago", item2: "Detalles de pago" });
  }, [setBreadcrumbData]);

  const formatDateToLongSpanish = (date: Date | undefined): string => {
    if (!date) return '';
    const formattedDate = format(date, "EEEE dd 'de' MMMM 'del' yyyy", { locale: es });
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1); // Capitalize the first letter
  };

  const handleDateSelection = () => {
    if (!activationDate) {
      setErrorMessage("Por favor selecciona una fecha de activación.");
      return;
    }

    const today = moment().startOf('day'); // Use moment to get today's date at midnight
    const selectedDate = moment(activationDate);

    if (selectedDate.isBefore(today)) {
      setErrorMessage("La fecha de activación debe ser hoy o una fecha futura.");
      return;
    }

    setShowDateModal(false);
    setShowSummaryModal(true);
    setErrorMessage(null);
  };

  const handleActivateClass = async () => {
    if (!selectedClass || !activationDate) {
      setErrorMessage("Por favor selecciona una fecha de activación.");
      return;
    }
    try {
      const payload = {
        userId: user?.userId,
        packetId: selectedClass.packageId,
        quantity: selectedClass.quantity,
        activationDate: activationDate.toISOString().split('T')[0],
        paymentDetailId: selectedClass.paymentDetailId,
      };
      const response = await post(payload);
      setSuccessMessage(response?.message || "Clase activada exitosamente.");
      setShowWarningModal(false);
      setShowSuccessModal(true);
      refetch(); // Refresh the data table
      setErrorMessage(null);

      emitter.emit('paymentCompleted', { paymentId: payload.paymentDetailId });
    } catch (error: any) {
      console.error("Error al activar la clase:", error);
      setErrorModalMessage(error?.response?.data?.message || "Hubo un error al activar la clase. Inténtalo nuevamente.");
      setShowErrorModal(true);
    } finally {
      setShowWarningModal(false);
    }
  };

  const openDateModal = (rowData: any) => {
    const today = moment().startOf('day');
    setActivationDate(today.toDate());
    setSelectedClass(rowData);
    setShowDateModal(true);
    setErrorMessage(null);
  };

  const closeDateModal = () => {
    setShowFooter(false); // Reset footer visibility when closing the modal
    const today = moment().startOf('day'); // Use moment to reset to today's date
    setActivationDate(today.toDate());
    setShowDateModal(false);
    setShowSummaryModal(false);
    setShowWarningModal(false);
    setErrorMessage(null);
  };

  const columns = [
    { field: "paymentDate", header: "Fecha de Pago", body: (rowData: any) => rowData.paymentDate },
    { field: "description", header: "Descripción", body: (rowData: any) => rowData.description },
    { field: "customerName", header: "Cliente", body: (rowData: any) => rowData.customerName },
    { field: "customerEmail", header: "Correo Electrónico", body: (rowData: any) => rowData.customerEmail },
    { field: "quantity", header: "Cantidad", body: (rowData: any) => rowData.quantity },
    { field: "amount", header: "Monto Total", body: (rowData: any) => `S/. ${Number(rowData.amount).toFixed(2)}` },
    {
      field: "isActivated",
      header: "Estado",
      className: "text-center", // Ensure the className is applied
      body: (rowData: any) => (
        <div>
          {rowData.isActivated ? (
            <span className="badge badge-success">Activado</span>
          ) : (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openDateModal(rowData)}
            >
              Activar Paquete/Membresía
            </button>
          )}
        </div>
      ),
      style: { position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 1 }, // Ensure the style is applied
    },
  ];

  return (
    <>
      <div className="">
        <div className="d-flex w-100 mt-3 mb-3">
          <div className="row w-100">
            <div className="col">
              <h4>Detalles de pago</h4>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <DataTableWrapper
            data={data || []}
            columns={columns}
            paginator
            showGridlines
            rowsPerPage={rows}
            totalRecords={totalElements}
            first={first}
            onPageChange={onPageChange}
            emptyMessage="No se encontraron detalles de pago."
          />
        </div>
      </div>
      <ConfirmActionModal
        show={showSummaryModal}
        onClose={closeDateModal}
        onConfirm={() => {
          setShowSummaryModal(false);
          setShowWarningModal(true);
        }}
        loading={false}
        title="Confirmar Activación"
      >
        <div className="text-center">
          <p className="mb-3">Tu clase se activará con la siguiente información:</p>
          <div className="table-responsive"> {/* Add responsive container */}
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th>Clase</th>
                  <td>{selectedClass?.description}</td>
                </tr>
                <tr>
                  <th>Monto</th>
                  <td>S/. {Number(selectedClass?.amount).toFixed(2)}</td>
                </tr>
                <tr>
                  <th>{isDesktop ? "Fecha de Activación": "Inicia el"}</th>
                  <td>{formatDateToLongSpanish(activationDate)}</td>
                </tr>
                <tr>
                  <th>Cliente</th>
                  <td>{selectedClass?.customerName}</td>
                </tr>
                <tr>
                  <th>{isDesktop ? "Correo Electrónico": "Correo"}</th>
                  <td>{selectedClass?.customerEmail}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {errorMessage && (
            <div className="alert alert-danger mt-3" role="alert">
              {errorMessage}
            </div>
          )}
        </div>
      </ConfirmActionModal>

      <BaseModal
        show={showDateModal}
        onClose={closeDateModal}
        title="Seleccionar Fecha de Activación"
        footer={
          showFooter && ( // Use showFooter to control footer visibility
            <>
              <button
                onClick={closeDateModal}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button onClick={handleDateSelection} className="btn btn-primary">
                Confirmar Fecha Seleccionada
              </button>
            </>
          )
        }
      >
        <div className="text-center">
          <p className="mb-3">¿Deseas activar la clase hoy o seleccionar una fecha personalizada?</p>
          { !showFooter && (
            <div className="d-flex justify-content-center gap-3 mt-3">
              <button
                className="btn btn-success"
                onClick={() => {
                  const today = moment().startOf('day');
                  setActivationDate(today.toDate());
                  setErrorMessage(null);
                  setShowFooter(false); // Hide footer for quick activation
                  setTimeout(() => handleDateSelection(), 0); // Ensure state is updated before calling handleDateSelection
                }}
              >
                Activar Hoy
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setActivationDate(undefined); // Clear the date for custom selection
                  setErrorMessage(null);
                  setShowFooter(true); // Show footer for custom date selection
                }}
              >
                Seleccionar Fecha
              </button>
            </div>
          )}
          {showFooter && (
            <div className="w-100 mt-3">
              <DatePicker
                value={activationDate}
                onChange={(date) => setActivationDate(date)}
                blockPastDates={true}
              />
              {
                activationDate && (
                  <div className="alert alert-warning d-flex align-items-center m-0 mt-3" role="alert">
                    <i className="bi bi-exclamation-triangle-fill fs-4 text-warning"></i>
                    <div className="">
                      No vas a poder realizar reservas hasta que llegue la fecha que estás indicando.
                    </div>
                  </div>
                )
              }
            </div>
          )}
          {errorMessage && (
            <div className="alert alert-danger mt-3" role="alert">
              {errorMessage}
            </div>
          )}
        </div>
      </BaseModal>

      <ConfirmActionModal
        show={showWarningModal}
        onClose={closeDateModal}
        onConfirm={handleActivateClass}
        loading={loadingModal}
        title="Advertencia"
        text='Recuerda que no vas a poder realizar reservas hasta que llegue la fecha que estás indicando. Una vez que hagas click en "confirmar", no se podrá revertir.'
      />
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Activación Exitosa"
        text={successMessage || "La clase se activó correctamente."}
        onConfirm={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error en la Activación"
        text={errorModalMessage || "Hubo un error al activar la clase. Inténtalo nuevamente."}
        onRetry={() => setShowErrorModal(false)}
      />
    </>
  );
};

export default PaymentDetails;